import React, { createContext, useContext, useState, useEffect } from 'react';
import { ThemeProvider as MuiThemeProvider, createTheme } from '@mui/material/styles';
import { CssBaseline } from '@mui/material';
import { alpha } from '@mui/material/styles';

// Create theme context
const ThemeContext = createContext();

// Default light theme
const createLightTheme = () => createTheme({
  palette: {
    mode: 'light',
    primary: {
      main: '#0A84FF',  // iOS blue
      light: '#5856D6',
      dark: '#0964B8',
      soft: alpha('#0A84FF', 0.12)
    },
    success: {
      main: '#34C759',  // iOS green
      soft: alpha('#34C759', 0.12)
    },
    error: {
      main: '#FF3B30',  // iOS red
      soft: alpha('#FF3B30', 0.12)
    },
    info: {
      main: '#5856D6',  // iOS purple
      soft: alpha('#5856D6', 0.12)
    },
    grey: {
      soft: alpha('#8E8E93', 0.12)  // iOS grey
    },
    background: {
      default: '#F7F8FA',  // Slightly warmer background
      paper: '#ffffff'
    },
    text: {
      primary: '#111827',  // Darker, more contrasty text
      secondary: '#6B7280'  // Warmer grey
    }
  },
  typography: {
    fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, Helvetica, Arial, sans-serif',
    h4: {
      fontSize: '1.5rem',
      fontWeight: 600,
      letterSpacing: '-0.02em',
      lineHeight: 1.3
    },
    h6: {
      fontSize: '1rem',
      fontWeight: 600,
      letterSpacing: '-0.01em'
    },
    body1: {
      fontSize: '0.9375rem',
      letterSpacing: '-0.01em'
    },
    body2: {
      fontSize: '0.875rem',
      letterSpacing: '-0.01em'
    }
  },
  shape: {
    borderRadius: 12
  },
  components: {
    MuiCard: {
      styleOverrides: {
        root: {
          boxShadow: 'rgba(0, 0, 0, 0.03) 0px 2px 4px',
          border: '1px solid rgba(0, 0, 0, 0.05)',
          borderRadius: '12px',
          transition: 'all 0.2s ease-in-out'
        }
      }
    },
    MuiButton: {
      styleOverrides: {
        root: {
          textTransform: 'none',
          fontWeight: 600,
        },
      },
    },
  },
});

// Dark theme
const createDarkTheme = () => createTheme({
  palette: {
    mode: 'dark',
    primary: {
      main: '#0A84FF',  // iOS blue
      light: '#5856D6',
      dark: '#0964B8',
      soft: alpha('#0A84FF', 0.2)
    },
    success: {
      main: '#34C759',  // iOS green
      soft: alpha('#34C759', 0.2)
    },
    error: {
      main: '#FF3B30',  // iOS red
      soft: alpha('#FF3B30', 0.2)
    },
    info: {
      main: '#5856D6',  // iOS purple
      soft: alpha('#5856D6', 0.2)
    },
    grey: {
      soft: alpha('#8E8E93', 0.2)  // iOS grey
    },
    background: {
      default: '#121212',
      paper: '#1E1E1E'
    },
    text: {
      primary: '#EEEEEE',
      secondary: '#AAAAAA'
    }
  },
  typography: {
    fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, Helvetica, Arial, sans-serif',
    h4: {
      fontSize: '1.5rem',
      fontWeight: 600,
      letterSpacing: '-0.02em',
      lineHeight: 1.3
    },
    h6: {
      fontSize: '1rem',
      fontWeight: 600,
      letterSpacing: '-0.01em'
    },
    body1: {
      fontSize: '0.9375rem',
      letterSpacing: '-0.01em'
    },
    body2: {
      fontSize: '0.875rem',
      letterSpacing: '-0.01em'
    }
  },
  shape: {
    borderRadius: 12
  },
  components: {
    MuiCard: {
      styleOverrides: {
        root: {
          boxShadow: 'rgba(0, 0, 0, 0.2) 0px 2px 4px',
          border: '1px solid rgba(255, 255, 255, 0.05)',
          borderRadius: '12px',
          transition: 'all 0.2s ease-in-out'
        }
      }
    },
    MuiButton: {
      styleOverrides: {
        root: {
          textTransform: 'none',
          fontWeight: 600,
        },
      },
    },
  },
});

export const ThemeProvider = ({ children }) => {
  // Check localStorage for saved theme preference
  const savedMode = localStorage.getItem('themeMode');
  const [mode, setMode] = useState(savedMode || 'light');
  const [theme, setTheme] = useState(createLightTheme());

  // Update the theme when mode changes
  useEffect(() => {
    const newTheme = mode === 'dark' ? createDarkTheme() : createLightTheme();
    setTheme(newTheme);
    // Save to localStorage
    localStorage.setItem('themeMode', mode);
  }, [mode]);

  // Toggle between light and dark mode
  const toggleColorMode = () => {
    setMode(prevMode => prevMode === 'light' ? 'dark' : 'light');
  };

  return (
    <ThemeContext.Provider value={{ mode, toggleColorMode }}>
      <MuiThemeProvider theme={theme}>
        <CssBaseline />
        {children}
      </MuiThemeProvider>
    </ThemeContext.Provider>
  );
};

// Custom hook to use the theme context
export const useTheme = () => {
  const context = useContext(ThemeContext);
  if (!context) {
    throw new Error('useTheme must be used within a ThemeProvider');
  }
  return context;
}; 